import { Box, Icon, SxProps, Typography } from "@outschool/backpack";
import { faUnlock } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs, formatDateTime } from "@outschool/time";
import React from "react";

import LessonNumber from "./LessonNumber";
import UnitNumber from "./UnitNumber";

type UnitLessonHeaderProps = {
  lessonNumber?: number | null;
  unitNumber?: number | null;
  unitTitle?: string | null;
  showMeetingDate?: boolean;
  sx?: SxProps;
  unlocksAt?: Date | null;
} & MeetingDateAndTimeZone;

// If meetingDate is provided, timeZone must also be provided.
type MeetingDateAndTimeZone =
  | {
      meetingDate?: never;
      timeZone?: never;
    }
  | {
      meetingDate: Date | null;
      timeZone: string;
    };

export default function UnitLessonHeader({
  lessonNumber,
  meetingDate,
  timeZone,
  showMeetingDate = false,
  unitNumber,
  unitTitle,
  unlocksAt,
  sx,
}: UnitLessonHeaderProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\components\\syllabus\\UnitLessonHeader",
  );
  return (
    <Box>
      <Box
        flex
        sx={[
          {
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1em",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Box sx={{ width: "50%" }}>
          <LessonNumber
            lessonNumber={lessonNumber}
            prefix={
              !showMeetingDate
                ? undefined
                : meetingDate
                ? t("Meets {{meetingDate}}", {
                    meetingDate: formatDateTime(meetingDate, timeZone, true),
                  })
                : t("Meeting Not Yet Scheduled.")
            }
            prefixSx={
              showMeetingDate && !meetingDate
                ? { color: "error.700" }
                : undefined
            }
          />
        </Box>
        <Box sx={{ width: "50%" }}>
          <UnitNumber unitNumber={unitNumber} unitTitle={unitTitle} />
        </Box>
      </Box>
      {unlocksAt && dayjs().isBefore(unlocksAt) && (
        <Typography variant="caption">
          <Icon icon={faUnlock} />{" "}
          {t("Unlocks on {{unlocksAt}}", {
            unlocksAt: formatDateTime(unlocksAt, timeZone, true),
          })}
        </Typography>
      )}
    </Box>
  );
}
